import {
  Component,
  inject,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntil, catchError, of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SMEService } from '../../services/sme.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { AddressService } from '../../../../shared/services/address/address.service';
import { ToastType } from '../../../../shared/interfaces/common';
import {
  CompanyDetails,
  Properties,
} from '../../../../shared/models/company-details';
import {
  State,
  County,
  City,
  Zipcode,
} from '../../../../shared/models/address';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { ComboboxComponent } from '../../../../shared/components/combobox/combobox.component';

interface EditFormData {
  name: string;
  email: string;
  phone: string;
  preferredLanguage: string;
  isMilitaryVeteran: boolean | null;
  isVeteranSpouse: boolean | null;
  hasBusiness: 'yes' | 'no' | 'planning' | null;
  businessName: string;
  businessAddress: string;
  businessWebsite: string;
  businessType: string;
  businessEstablished: string;
  numberOfEmployees: number | null;
  applicationSummary: string;
  state: string;
  county: string;
  city: string;
  zipCode: string;
  stateId: number | null;
  countyId: number | null;
  cityId: number | null;
}

@Component({
  selector: 'app-business-info-edit',
  templateUrl: './business-info-edit.component.html',
  styleUrls: ['./business-info-edit.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ComboboxComponent],
})
export class BusinessInfoEditComponent
  extends MenuComponentBase
  implements OnInit, OnChanges
{
  @Input() businessProfile: CompanyDetails | null = null;
  @Output() saved = new EventEmitter<CompanyDetails | undefined>();
  @Output() cancelled = new EventEmitter<void>();

  private smeService = inject(SMEService);
  private commonService = inject(CommonService);
  private addressService = inject(AddressService);
  private translateService = inject(TranslateService);

  private citiesAndCountiesSub?: Subscription;
  private zipcodesSub?: Subscription;

  isLoading = false;
  isSaving = false;
  submitted = false;
  validationErrors: Record<string, string> = {};

  // Address data
  states: State[] = [];
  counties: County[] = [];
  cities: City[] = [];
  zipcodes: Zipcode[] = [];
  isLoadingStates = false;
  isLoadingCounties = false;
  isLoadingZipcodes = false;

  languages = [
    { value: 'english', label: 'English' },
    { value: 'spanish', label: 'Spanish' },
  ];

  readonly defaultBusinessTypeOptions = [
    { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
    { value: 'lp', label: 'Limited Partnership (LP)' },
    { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'c_corporation', label: 'C Corporation' },
    { value: 's_corporation', label: 'S Corporation' },
    { value: 'benefit_corporation', label: 'Benefit Corporation' },
    { value: 'close_corporation', label: 'Close Corporation' },
    { value: 'non_profit_corporation', label: 'Non-Profit Corporation' },
    { value: 'cooperative', label: 'Cooperative' },
    { value: 'other', label: 'Other' },
  ];

  businessTypeOptions: { value: string; label: string }[] = [
    ...this.defaultBusinessTypeOptions,
  ];

  form: EditFormData = {
    name: '',
    email: '',
    phone: '',
    preferredLanguage: '',
    isMilitaryVeteran: null,
    isVeteranSpouse: null,
    hasBusiness: null,
    businessName: '',
    businessAddress: '',
    businessWebsite: '',
    businessType: '',
    businessEstablished: '',
    numberOfEmployees: null,
    applicationSummary: '',
    state: '',
    county: '',
    city: '',
    zipCode: '',
    stateId: null,
    countyId: null,
    cityId: null,
  };

  get fullBusinessAddress(): string {
    const parts = [
      this.form.businessAddress,
      this.form.city,
      this.form.county,
      this.form.state,
      this.form.zipCode,
    ].filter(Boolean);
    return parts.join(', ');
  }

  get isBusinessEstablishedValid(): boolean {
    const val = String(this.form.businessEstablished ?? '').trim();
    if (!val) return false;
    const yearRegex = /^\d{4}$/;
    const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/;

    if (yearRegex.test(val)) {
      const year = parseInt(val, 10);
      const currentYear = new Date().getFullYear();
      return year <= currentYear;
    }

    if (dateRegex.test(val)) {
      const parts = val.split('/');
      const month = parseInt(parts[0], 10) - 1; // 0-indexed
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const inputDate = new Date(year, month, day);

      if (
        inputDate.getFullYear() !== year ||
        inputDate.getMonth() !== month ||
        inputDate.getDate() !== day
      ) {
        return false;
      }

      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
      return inputDate <= todayStart;
    }

    return false;
  }

  formatCityName(name: string): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatCityItem = (item: City | string | unknown): string => {
    if (!item) return '';
    const name = typeof item === 'string' ? item : (item as City)?.name || '';
    return this.formatCityName(name);
  };

  getStatePlaceholder(): string {
    if (this.isLoadingStates) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_STATES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE',
    );
  }

  getCountyPlaceholder(): string {
    if (!this.form.state) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE_FIRST',
      );
    }
    if (this.isLoadingCounties || this.counties.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_COUNTIES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_COUNTY',
    );
  }

  getCityPlaceholder(): string {
    if (!this.form.state) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE_FIRST',
      );
    }
    if (!this.form.county) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_COUNTY_FIRST',
      );
    }
    if (this.isLoadingCounties || this.cities.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_CITIES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_CITY',
    );
  }

  getZipPlaceholder(): string {
    if (!this.form.city) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_CITY_FIRST',
      );
    }
    if (this.isLoadingZipcodes) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_ZIPCODES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_ZIP',
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadStates();
    this.loadProfile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessProfile'] && !changes['businessProfile'].firstChange) {
      this.loadProfile();
    }
  }

  private resolveBusinessType(
    rawValue: unknown,
    optionsMap?: Record<string, string | number | boolean> | null,
  ): string {
    if (rawValue === null || rawValue === undefined) {
      return '';
    }
    const strVal = String(rawValue).trim();
    if (!strVal) return '';

    // 1. If options map provided (label -> value)
    if (optionsMap && typeof optionsMap === 'object') {
      // Direct lookup by label
      if (optionsMap[strVal] !== undefined) {
        return String(optionsMap[strVal]);
      }
      // Case-insensitive label lookup
      const labelEntry = Object.entries(optionsMap).find(
        ([lbl]) => lbl.toLowerCase() === strVal.toLowerCase(),
      );
      if (labelEntry) {
        return String(labelEntry[1]);
      }
      // Case-insensitive value lookup (in case rawValue is already the option key/value)
      const valEntry = Object.entries(optionsMap).find(
        ([, val]) => String(val).toLowerCase() === strVal.toLowerCase(),
      );
      if (valEntry) {
        return String(valEntry[1]);
      }
    }

    // 2. Check this.businessTypeOptions list
    const matchByVal = this.businessTypeOptions.find(
      (opt) => opt.value.toLowerCase() === strVal.toLowerCase(),
    );
    if (matchByVal) {
      return matchByVal.value;
    }

    const matchByLabel = this.businessTypeOptions.find(
      (opt) => opt.label.toLowerCase() === strVal.toLowerCase(),
    );
    if (matchByLabel) {
      return matchByLabel.value;
    }

    return strVal;
  }

  private loadStates(): void {
    this.isLoadingStates = true;
    this.addressService
      .getStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (states) => {
          this.states = (states || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
          this.isLoadingStates = false;
          if (this.form.state && !this.form.stateId) {
            this.resolveExistingLocation();
          }
        },
        error: () => {
          this.isLoadingStates = false;
        },
      });
  }

  private loadProfile(): void {
    if (this.businessProfile?.detail?.properties) {
      this.populateForm(this.businessProfile.detail.properties);
    } else {
      this.commonService.triggerToast({
        message: 'No business profile details provided to edit',
        type: ToastType.Error,
      });
    }
  }

  private populateForm(properties: Properties[]): void {
    const get = (key: string): unknown => {
      const prop = properties.find((p) => p.property === key);
      return prop?.value ?? null;
    };

    // Reset options to default before inspecting properties
    this.businessTypeOptions = [...this.defaultBusinessTypeOptions];

    const busTypeProp = properties.find((p) => p.property === 'business_type');
    if (
      busTypeProp?.options &&
      typeof busTypeProp.options === 'object' &&
      Object.keys(busTypeProp.options).length > 0
    ) {
      this.businessTypeOptions = Object.entries(busTypeProp.options).map(
        ([label, value]) => ({
          value: String(value),
          label,
        }),
      );
    }

    this.form.name = (get('name') as string) ?? '';
    this.form.email = (get('email') as string) ?? '';
    const rawPhone = get('phone') ?? get('phone_number');
    this.form.phone =
      rawPhone !== null && rawPhone !== undefined ? String(rawPhone) : '';
    const lang = get('preferred_language');
    this.form.preferredLanguage = lang ? String(lang).toLowerCase() : '';
    this.form.applicationSummary = (get('application_summary') as string) ?? '';
    this.form.businessName =
      ((get('company_name') ?? get('business_name')) as string) ?? '';
    this.form.businessAddress = (get('business_address') as string) ?? '';
    this.form.businessWebsite = (get('business_website') as string) ?? '';
    this.form.businessType = this.resolveBusinessType(
      busTypeProp?.value,
      busTypeProp?.options,
    );

    // If resolved value is present but not in businessTypeOptions, add it dynamically
    if (
      this.form.businessType &&
      !this.businessTypeOptions.some(
        (opt) => opt.value === this.form.businessType,
      )
    ) {
      this.businessTypeOptions.push({
        value: this.form.businessType,
        label: String(busTypeProp?.value ?? this.form.businessType),
      });
    }
    const businessEst = get('business_established');
    this.form.businessEstablished =
      businessEst !== null && businessEst !== undefined
        ? String(businessEst)
        : '';
    const emp = get('number_of_employees');
    this.form.numberOfEmployees = emp !== null ? Number(emp) : null;

    const hasBusiness = get('has_business');
    this.form.hasBusiness = hasBusiness
      ? (String(hasBusiness).toLowerCase() as EditFormData['hasBusiness'])
      : null;

    // Veteran flags stored as true/false booleans or "Yes"/"No" strings
    const vet = get('is_military_veteran');
    if (vet === true || vet === 'true' || vet === 'Yes') {
      this.form.isMilitaryVeteran = true;
    } else if (vet === false || vet === 'false' || vet === 'No') {
      this.form.isMilitaryVeteran = false;
    } else {
      this.form.isMilitaryVeteran = null;
    }

    const vetSpouse = get('is_veteran_spouse');
    if (vetSpouse === true || vetSpouse === 'true' || vetSpouse === 'Yes') {
      this.form.isVeteranSpouse = true;
    } else if (
      vetSpouse === false ||
      vetSpouse === 'false' ||
      vetSpouse === 'No'
    ) {
      this.form.isVeteranSpouse = false;
    } else {
      this.form.isVeteranSpouse = null;
    }

    // Load location
    const stateName = get('state') as string;
    const countyName = get('county') as string;
    const cityName = get('city') as string;
    const zipCode = get('zip_code') as string;

    if (stateName) {
      this.form.state = stateName;
      this.form.county = countyName ?? '';
      this.form.city = cityName ?? '';
      this.form.zipCode = zipCode ?? '';

      if (this.states.length > 0) {
        this.resolveExistingLocation();
      }
    }
  }

  private resolveExistingLocation(): void {
    if (!this.form.state || this.states.length === 0) return;

    const stateObj = this.states.find(
      (s) => s.name.toLowerCase() === this.form.state.toLowerCase(),
    );
    if (!stateObj) return;

    this.form.stateId = stateObj.id;
    this.form.state = stateObj.name;

    this.isLoadingCounties = true;
    this.citiesAndCountiesSub?.unsubscribe();
    this.citiesAndCountiesSub = this.addressService
      .getCitiesAndCountiesByStateIds([stateObj.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ counties, cities }) => {
          this.counties = (counties || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
          this.cities = (cities || [])
            .slice()
            .sort((a, b) =>
              this.formatCityName(a.name).localeCompare(
                this.formatCityName(b.name),
                undefined,
                { sensitivity: 'base' },
              ),
            );
          this.isLoadingCounties = false;

          const countyObj = this.counties.find(
            (c) =>
              c.name.toLowerCase() === (this.form.county ?? '').toLowerCase(),
          );
          if (countyObj) {
            this.form.countyId = countyObj.id;
            this.form.county = countyObj.name;
          }

          const cityObj = this.cities.find(
            (c) =>
              c.name.toLowerCase() === (this.form.city ?? '').toLowerCase(),
          );
          if (cityObj) {
            this.form.cityId = cityObj.id;
            this.form.city = cityObj.name;

            // Load zipcodes
            this.isLoadingZipcodes = true;
            this.zipcodesSub?.unsubscribe();
            this.zipcodesSub = this.addressService
              .getZipcodes(cityObj.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (zips) => {
                  this.zipcodes = (zips || [])
                    .slice()
                    .sort((a, b) => a.code.localeCompare(b.code));
                  this.isLoadingZipcodes = false;
                },
                error: () => {
                  this.isLoadingZipcodes = false;
                },
              });
          }
        },
        error: () => {
          this.isLoadingCounties = false;
        },
      });
  }

  onStateChange(): void {
    this.form.countyId = null;
    this.form.county = '';
    this.form.cityId = null;
    this.form.city = '';
    this.form.zipCode = '';
    this.counties = [];
    this.cities = [];
    this.zipcodes = [];

    this.citiesAndCountiesSub?.unsubscribe();
    this.zipcodesSub?.unsubscribe();

    if (!this.form.state) {
      this.form.stateId = null;
      return;
    }

    const state = this.states.find(
      (s) => s.name.toLowerCase() === this.form.state.toLowerCase(),
    );
    if (!state) return;
    this.form.stateId = state.id;

    this.isLoadingCounties = true;
    this.citiesAndCountiesSub = this.addressService
      .getCitiesAndCountiesByStateIds([state.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ counties, cities }) => {
          this.counties = (counties || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
          this.cities = (cities || [])
            .slice()
            .sort((a, b) =>
              this.formatCityName(a.name).localeCompare(
                this.formatCityName(b.name),
                undefined,
                { sensitivity: 'base' },
              ),
            );
          this.isLoadingCounties = false;
        },
        error: () => {
          this.isLoadingCounties = false;
        },
      });
  }

  onCountyChange(): void {
    this.form.cityId = null;
    this.form.city = '';
    this.form.zipCode = '';
    this.zipcodes = [];
    this.zipcodesSub?.unsubscribe();

    if (!this.form.county) {
      this.form.countyId = null;
      return;
    }

    const county = this.counties.find(
      (c) => c.name.toLowerCase() === this.form.county.toLowerCase(),
    );
    if (!county) return;
    this.form.countyId = county.id;
  }

  onCityChange(): void {
    this.form.zipCode = '';
    this.zipcodes = [];
    this.zipcodesSub?.unsubscribe();

    if (!this.form.city) {
      this.form.cityId = null;
      return;
    }

    const city = this.cities.find(
      (c) => c.name.toLowerCase() === this.form.city.toLowerCase(),
    );
    if (!city) {
      this.form.cityId = null;
      return;
    }
    this.form.cityId = city.id;

    this.isLoadingZipcodes = true;
    this.zipcodesSub = this.addressService
      .getZipcodes(city.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zips) => {
          this.zipcodes = (zips || [])
            .slice()
            .sort((a, b) => a.code.localeCompare(b.code));
          this.isLoadingZipcodes = false;
        },
        error: () => {
          this.isLoadingZipcodes = false;
        },
      });
  }

  onHasBusinessChange(): void {
    if (this.form.hasBusiness !== 'yes') {
      this.form.businessName = '';
      this.form.businessWebsite = '';
      this.form.businessType = '';
      this.form.businessEstablished = '';
      this.form.numberOfEmployees = null;
    }
  }

  blockDecimalAndSigns(event: KeyboardEvent): void {
    if (['.', 'e', 'E', '-', '+'].includes(event.key)) {
      event.preventDefault();
    }
  }

  blockPasteDecimal(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    const pastedText = clipboardData.getData('text');
    if (
      pastedText &&
      (pastedText.includes('.') ||
        pastedText.includes('e') ||
        pastedText.includes('E') ||
        pastedText.includes('-') ||
        pastedText.includes('+'))
    ) {
      event.preventDefault();
    }
  }

  get hasErrors(): boolean {
    return Object.keys(this.validationErrors).length > 0;
  }

  isFormValid(): boolean {
    this.submitted = true;
    this.validationErrors = {};

    if (!this.form.name?.trim()) {
      this.validationErrors['name'] = 'Name is required';
    }
    if (!this.form.email?.trim()) {
      this.validationErrors['email'] = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.form.email.trim())) {
        this.validationErrors['email'] = 'Enter a valid email address';
      }
    }

    if (this.form.phone?.trim()) {
      const phoneRegex = /^\+?[\d\s-()]{10,}$/;
      const digits = this.form.phone.replace(/\D/g, '');
      if (!phoneRegex.test(this.form.phone.trim()) || digits.length < 10) {
        this.validationErrors['phone'] = 'Enter a valid phone number';
      }
    }

    if (!this.form.preferredLanguage) {
      this.validationErrors['preferredLanguage'] =
        'Preferred Language is required';
    }
    if (!this.form.hasBusiness) {
      this.validationErrors['hasBusiness'] = 'Business Status is required';
    }
    if (!this.form.applicationSummary?.trim()) {
      this.validationErrors['applicationSummary'] =
        'Application Summary is required';
    }
    if (!this.form.businessAddress?.trim()) {
      this.validationErrors['businessAddress'] = 'Address is required';
    }
    if (!this.form.state?.trim()) {
      this.validationErrors['state'] = 'State is required';
      this.validationErrors['stateId'] = 'State is required';
    }
    if (!this.form.county?.trim()) {
      this.validationErrors['county'] = 'County is required';
      this.validationErrors['countyId'] = 'County is required';
    }
    if (!this.form.city?.trim()) {
      this.validationErrors['city'] = 'City is required';
      this.validationErrors['cityId'] = 'City is required';
    }
    if (!this.form.zipCode?.trim()) {
      this.validationErrors['zipCode'] = 'Zip Code is required';
    }

    if (this.form.hasBusiness === 'yes') {
      if (!this.form.businessName?.trim()) {
        this.validationErrors['businessName'] = 'Business Name is required';
      }
      if (this.form.businessWebsite?.trim()) {
        const urlRegex =
          /^(https?:\/\/)?([a-zA-Z0-9.-]+)\.([a-zA-Z]{2,6})(\/[a-zA-Z0-9_.-]*)*\/?$/;
        if (!urlRegex.test(this.form.businessWebsite.trim())) {
          this.validationErrors['businessWebsite'] = 'Enter a valid URL';
        }
      }
      if (!this.form.businessType?.trim()) {
        this.validationErrors['businessType'] = 'Business Type is required';
      }
      const establishedVal = String(this.form.businessEstablished ?? '').trim();
      if (!establishedVal) {
        this.validationErrors['businessEstablished'] =
          'Established Year/Date is required';
      } else {
        const val = establishedVal;
        const yearRegex = /^\d{4}$/;
        const dateRegex =
          /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/;

        if (!yearRegex.test(val) && !dateRegex.test(val)) {
          this.validationErrors['businessEstablished'] =
            'Enter a valid year (YYYY) or date (MM/DD/YYYY)';
        } else if (yearRegex.test(val)) {
          const year = parseInt(val, 10);
          const currentYear = new Date().getFullYear();
          if (year > currentYear) {
            this.validationErrors['businessEstablished'] =
              'Established year cannot be in the future';
          }
        } else {
          const parts = val.split('/');
          const month = parseInt(parts[0], 10) - 1; // 0-indexed
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          const inputDate = new Date(year, month, day);

          if (
            inputDate.getFullYear() !== year ||
            inputDate.getMonth() !== month ||
            inputDate.getDate() !== day
          ) {
            this.validationErrors['businessEstablished'] =
              'Enter a valid calendar date';
          } else {
            const today = new Date();
            const todayStart = new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate(),
            );
            if (inputDate > todayStart) {
              this.validationErrors['businessEstablished'] =
                'Established date cannot be in the future';
            }
          }
        }
      }
      if (
        this.form.numberOfEmployees === null ||
        this.form.numberOfEmployees === undefined
      ) {
        this.validationErrors['numberOfEmployees'] =
          'Number of Employees is required';
      } else if (this.form.numberOfEmployees < 0) {
        this.validationErrors['numberOfEmployees'] = 'Must be 0 or positive';
      } else if (!Number.isInteger(Number(this.form.numberOfEmployees))) {
        this.validationErrors['numberOfEmployees'] = 'Must be a whole number';
      }
    }

    if (
      this.form.isMilitaryVeteran === null ||
      this.form.isMilitaryVeteran === undefined
    ) {
      this.validationErrors['isMilitaryVeteran'] = 'Veteran status is required';
    }
    if (
      this.form.isVeteranSpouse === null ||
      this.form.isVeteranSpouse === undefined
    ) {
      this.validationErrors['isVeteranSpouse'] = 'Spouse status is required';
    }

    return !this.hasErrors;
  }

  save(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.isSaving = true;

    const originalProperties = this.businessProfile?.detail?.properties ?? [];
    const getOriginal = (key: string): unknown => {
      const prop = originalProperties.find((p) => p.property === key);
      return prop?.value ?? null;
    };

    const properties: { property: string; value: unknown }[] = [];

    // Helper to normalize values (convert empty strings/whitespaces to null)
    const normalize = (val: unknown): unknown => {
      if (val === null || val === undefined) {
        return null;
      }
      if (typeof val === 'string') {
        const trimmed = val.trim();
        return trimmed === '' ? null : trimmed;
      }
      return val;
    };

    // Helper to add property if value changed
    const addIfChanged = (key: string, newValue: unknown): void => {
      const orig = normalize(getOriginal(key));
      const normNew = normalize(newValue);
      if (orig !== normNew) {
        properties.push({ property: key, value: normNew });
      }
    };

    addIfChanged('name', this.form.name);
    addIfChanged('email', this.form.email);

    const phoneOrig = normalize(
      getOriginal('phone') ?? getOriginal('phone_number'),
    );
    const phoneNew = normalize(this.form.phone);
    if (phoneOrig !== phoneNew) {
      const phoneKey = originalProperties.some(
        (p) => p.property === 'phone_number',
      )
        ? 'phone_number'
        : 'phone';
      properties.push({ property: phoneKey, value: phoneNew });
    }

    // preferred_language is compared case-insensitively/lowercased
    const langOrig = normalize(getOriginal('preferred_language'));
    const langOrigStr = langOrig ? String(langOrig).toLowerCase() : null;
    const langNew = normalize(this.form.preferredLanguage);
    const langNewStr = langNew ? String(langNew).toLowerCase() : null;
    if (langOrigStr !== langNewStr) {
      properties.push({ property: 'preferred_language', value: langNewStr });
    }

    addIfChanged('application_summary', this.form.applicationSummary);

    // has_business is compared case-insensitively/lowercased
    const hasBusOrig = normalize(getOriginal('has_business'));
    const hasBusOrigStr = hasBusOrig ? String(hasBusOrig).toLowerCase() : null;
    const hasBusNew = normalize(this.form.hasBusiness);
    const hasBusNewStr = hasBusNew ? String(hasBusNew).toLowerCase() : null;
    if (hasBusOrigStr !== hasBusNewStr) {
      properties.push({ property: 'has_business', value: hasBusNewStr });
    }

    addIfChanged('business_address', this.form.businessAddress);
    addIfChanged('state', this.form.state);
    addIfChanged('county', this.form.county);
    addIfChanged('city', this.form.city);
    addIfChanged('zip_code', this.form.zipCode);

    // is_military_veteran (compare parsed boolean status)
    const vetOrig = getOriginal('is_military_veteran');
    let vetOrigBool: boolean | null = null;
    if (vetOrig === true || vetOrig === 'true' || vetOrig === 'Yes') {
      vetOrigBool = true;
    } else if (vetOrig === false || vetOrig === 'false' || vetOrig === 'No') {
      vetOrigBool = false;
    }
    const vetNew = this.form.isMilitaryVeteran;
    if (vetOrigBool !== vetNew) {
      properties.push({ property: 'is_military_veteran', value: vetNew });
    }

    // is_veteran_spouse (compare parsed boolean status)
    const spouseOrig = getOriginal('is_veteran_spouse');
    let spouseOrigBool: boolean | null = null;
    if (spouseOrig === true || spouseOrig === 'true' || spouseOrig === 'Yes') {
      spouseOrigBool = true;
    } else if (
      spouseOrig === false ||
      spouseOrig === 'false' ||
      spouseOrig === 'No'
    ) {
      spouseOrigBool = false;
    }
    const spouseNew = this.form.isVeteranSpouse;
    if (spouseOrigBool !== spouseNew) {
      properties.push({ property: 'is_veteran_spouse', value: spouseNew });
    }

    // business specific fields (only sent when hasBusiness === 'yes')
    const compNameOrig = normalize(
      getOriginal('company_name') ?? getOriginal('business_name'),
    );
    const compNameNew =
      this.form.hasBusiness === 'yes'
        ? normalize(this.form.businessName)
        : null;
    if (compNameOrig !== compNameNew) {
      properties.push({ property: 'company_name', value: compNameNew });
    }

    const webOrig = normalize(getOriginal('business_website'));
    const webNew =
      this.form.hasBusiness === 'yes'
        ? normalize(this.form.businessWebsite)
        : null;
    if (webOrig !== webNew) {
      properties.push({ property: 'business_website', value: webNew });
    }

    const busTypeProp = originalProperties.find(
      (p) => p.property === 'business_type',
    );
    const busTypeOrig = normalize(
      this.resolveBusinessType(busTypeProp?.value, busTypeProp?.options),
    );
    const busTypeNew =
      this.form.hasBusiness === 'yes'
        ? normalize(this.form.businessType)
        : null;
    if (busTypeOrig !== busTypeNew) {
      properties.push({ property: 'business_type', value: busTypeNew });
    }

    const estOrig = normalize(getOriginal('business_established'));
    const estNew =
      this.form.hasBusiness === 'yes'
        ? normalize(this.form.businessEstablished)
        : null;
    if (estOrig !== estNew) {
      properties.push({ property: 'business_established', value: estNew });
    }

    const empOrig = getOriginal('number_of_employees');
    const empOrigNum =
      empOrig !== null && empOrig !== undefined ? Number(empOrig) : null;
    const empNew =
      this.form.hasBusiness === 'yes'
        ? this.form.numberOfEmployees !== null
          ? Number(this.form.numberOfEmployees)
          : null
        : null;
    if (empOrigNum !== empNew) {
      properties.push({ property: 'number_of_employees', value: empNew });
    }

    if (properties.length === 0) {
      this.isSaving = false;
      this.commonService.triggerToast({
        message: 'Business details updated successfully',
        type: ToastType.Success,
      });
      this.saved.emit(this.businessProfile ?? undefined);
      return;
    }

    const payload = { properties };

    this.smeService
      .updateSMEBusinessRecord(payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.isSaving = false;
          this.commonService.triggerToast({
            message: 'Failed to update business information',
            type: ToastType.Error,
          });
          return of(null);
        }),
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.isSaving = false;
            this.commonService.triggerToast({
              message: 'Business information updated successfully',
              type: ToastType.Success,
            });
            this.saved.emit(result);
          }
        },
      });
  }

  goBack(): void {
    this.cancelled.emit();
  }
}
